import React, { useMemo } from 'react';
import * as Yup from 'yup';

import { FeeAmendmentRequest, FeeDetails, FeeSource } from '@ndvr/billing-api';

import { FormSheet } from '@/components/FormSheet';
import { formatCurrency, useRequiredParams } from 'utils';
import { useCreateFeeAmendment } from 'views/billing/billing-queries';
import { BasicFeeDetails } from 'views/household/BillingHistoryTab/BasicFeeDetails';
import { FormMoneyInput, FormSection, Ribbon, Text, VerticalLayout } from 'widgets';

export const FeeAmendmentDrawer = ({ fee, onClose }: { fee: FeeDetails; onClose: () => void }) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { mutate, isPending } = useCreateFeeAmendment(globalAccountGUID);

    const outstandingAmount = Number(fee.directFeeAmount) - Number(fee.directSettledFeeAmount);

    const feeAmendmentFormSchema = useMemo(() => {
        let feeAmendmentAmount = Yup.number()
            .required('Fee amendment amount is required')
            .typeError('Fee amendment amount is required')
            .test('non-zero', 'Fee amendment amount cannot be zero', (value) => value !== 0);
        if (fee.feeSource === FeeSource.POSITIVE_AMENDMENT) {
            feeAmendmentAmount = feeAmendmentAmount.max(0, 'Positive amendments can only be applied to the base fee');
        }
        if (outstandingAmount > 0) {
            feeAmendmentAmount = feeAmendmentAmount.min(
                -outstandingAmount,
                'The negative adjustment may not exceed the outstanding amount',
            );
        } else {
            feeAmendmentAmount = feeAmendmentAmount.min(
                -Number(fee.directSettledFeeAmount),
                'The negative adjustment may not exceed the settled amount',
            );
        }
        return Yup.object({ feeAmendmentAmount });
    }, [fee.directSettledFeeAmount, fee.feeSource, outstandingAmount]);

    return (
        <FormSheet
            title="Create New Fee Amendment"
            onClose={onClose}
            formSchema={feeAmendmentFormSchema}
            pending={isPending}
            submitLabel="Create"
            onSubmit={({ feeAmendmentAmount }: Pick<FeeAmendmentRequest, 'feeAmendmentAmount'>, close) =>
                mutate({ amendedFeeEntryGUID: fee.feeEntry.feeEntryGUID, feeAmendmentAmount }, { onSuccess: close })
            }
        >
            <BasicFeeDetails fee={fee} showDirectFeeAmounts />
            <FormSection>
                {outstandingAmount > 0 ? (
                    <VerticalLayout $gap={4}>
                        <Text largeBody>
                            Outstanding amount: {formatCurrency(outstandingAmount, fee.feeEntry.currency, 2)}
                        </Text>
                        <Text helper>
                            Negative amendments of unsettled fees may not exceed the outstanding amount. The settled
                            amount may only be refunded if the outstanding amount is zero.
                        </Text>
                    </VerticalLayout>
                ) : (
                    <VerticalLayout $gap={6}>
                        <Text largeBody>
                            Settled amount: {formatCurrency(fee.directSettledFeeAmount, fee.feeEntry.currency, 2)}
                        </Text>
                        <Ribbon intent="warning">
                            <Text>
                                This fee is already settled. A negative amendment should only be applied if that amount
                                had been refunded. Negative amendments of settled fees may not exceed the settled
                                amount.
                            </Text>
                        </Ribbon>
                    </VerticalLayout>
                )}
                <FormMoneyInput
                    vertical
                    name="feeAmendmentAmount"
                    label="Fee amendment amount"
                    decimalScale={2}
                    allowNegative
                />
            </FormSection>
        </FormSheet>
    );
};
